import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileText, Inbox, Reply, Share2, Trash2, Flag } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TypeBadge, StatusBadge, MinorBadge } from "../components/Badges";

export default function MyReports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/my/reports")
      .then((res) => setReports(res.data.results))
      .catch((e) => {
        if (e.response?.status === 401) {
          localStorage.removeItem("token");
          toast.error("Tu sesión expiró. Inicia sesión nuevamente.");
          navigate("/login");
        } else {
          toast.error("No se pudieron cargar tus reportes. Intenta de nuevo.");
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-[#0F172A]">Mis reportes</h1>
        <Link to="/reportar/desaparecida" data-testid="myreports-new-btn"
          className="flex items-center gap-1.5 h-11 px-4 rounded-xl bg-[#1E3A8A] text-white text-sm font-semibold">
          <Plus className="w-4 h-4" /> Nuevo
        </Link>
      </div>
      <p className="text-sm text-[#475569]">Hola, {user?.first_name}.</p>

      {loading ? (
        <p className="text-[#475569]">Cargando…</p>
      ) : reports.length === 0 ? (
        <div className="text-center py-12 text-[#475569]" data-testid="myreports-empty">
          <FileText className="w-10 h-10 mx-auto mb-3 text-[#CBD5E1]" />
          Aún no has creado reportes.
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <div key={r.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid={`myreport-${r.public_case_code}`}>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <TypeBadge caseType={r.case_type} label={r.case_type_label} />
                {r.is_minor_case && <MinorBadge />}
                <StatusBadge label={r.status_label} />
              </div>
              <h3 className="font-heading font-semibold">{[r.first_name, r.person?.last_name].filter(Boolean).join(" ") || "Sin nombre"}</h3>
              <p className="text-xs font-mono text-[#94A3B8]">{r.public_case_code}</p>
              <div className="flex gap-3 mt-3">
                <Link to={`/caso/${r.public_case_code}`} className="text-sm text-[#1E3A8A] font-medium underline">Ver ficha</Link>
                <Link to={`/mis-reportes/${r.id}/editar`} data-testid={`edit-${r.public_case_code}`} className="text-sm text-[#1E3A8A] font-medium underline">Editar</Link>
              </div>
              <CaseContacts caseId={r.id} code={r.public_case_code} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const C_STATUS = {
  new: "Nueva", notified_reporter: "Notificada", viewed_by_reporter: "Vista",
  reporter_replied: "Respondida", more_info_requested: "Solicitaste más info",
  contact_shared_by_reporter: "Compartiste tu contacto", contact_shared_mutual: "Contacto mutuo",
  discarded: "Descartada", abuse_reported: "Abuso reportado", closed: "Cerrada", blocked: "Bloqueada",
};

function CaseContacts({ caseId, code }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [reply, setReply] = useState({});

  const load = () => api.get(`/my/reports/${caseId}/contacts`).then((r) => setItems(r.data.results));
  const toggle = () => { if (!open && items === null) load(); setOpen(!open); };

  const doReply = async (id, more) => {
    const msg = (reply[id] || "").trim();
    if (!msg) { toast.error("Escribe un mensaje."); return; }
    await api.post(`/my/contacts/${id}/reply`, { message: msg, more_info: more });
    toast.success(more ? "Solicitud de más información enviada." : "Respuesta enviada.");
    setReply({ ...reply, [id]: "" }); load();
  };
  const act = async (id, path, msg) => { await api.post(`/my/contacts/${id}/${path}`, {}); toast.success(msg); load(); };

  return (
    <div className="mt-3 border-t border-[#F1F5F9] pt-3">
      <button onClick={toggle} data-testid={`contacts-toggle-${code}`}
        className="flex items-center gap-1.5 text-sm font-medium text-[#003893]">
        <Inbox className="w-4 h-4" /> {open ? "Ocultar solicitudes" : "Ver solicitudes de contacto"}
      </button>
      {open && (
        <div className="mt-3 space-y-3" data-testid={`contacts-panel-${code}`}>
          {items === null && <p className="text-sm text-[#94A3B8]">Cargando…</p>}
          {items && items.length === 0 && <p className="text-sm text-[#94A3B8]">Aún no hay solicitudes para este reporte.</p>}
          {items && items.map((c) => (
            <div key={c.id} className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3 space-y-2" data-testid={`contact-item-${c.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold px-2 py-0.5 rounded bg-[#EFF6FF] text-[#1E40AF]">
                  {c.request_type === "think_relative" ? "Cree ser familiar" : "Tiene información"}
                </span>
                <span className="text-xs font-semibold px-2 py-0.5 rounded bg-white border border-[#E2E8F0] text-[#475569]">{C_STATUS[c.status] || c.status}</span>
              </div>
              <p className="text-sm font-medium text-[#0F172A]">{c.requester_first_name} {c.requester_last_name || ""}</p>
              {c.relationship_claim && <p className="text-xs text-[#475569]">Relación declarada: {c.relationship_claim}</p>}
              <p className="text-sm text-[#0F172A]">{c.message}</p>
              {c.responder_share_consent ? (
                <p className="text-xs text-[#065F46]" data-testid={`contact-shared-info-${c.id}`}>
                  Esta persona autorizó compartir su contacto: {c.requester_phone || "—"} {c.requester_email ? `· ${c.requester_email}` : ""}
                </p>
              ) : (
                <p className="text-xs text-[#94A3B8]">Esta persona no autorizó compartir su contacto. Puedes responder de forma protegida.</p>
              )}
              {c.reporter_reply && <p className="text-xs text-[#475569]">Tu respuesta: {c.reporter_reply}</p>}

              {!["discarded", "closed", "blocked"].includes(c.status) && (
                <>
                  <textarea value={reply[c.id] || ""} onChange={(e) => setReply({ ...reply, [c.id]: e.target.value })}
                    rows={2} placeholder="Escribe una respuesta protegida…" data-testid={`contact-reply-input-${c.id}`}
                    className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm bg-white" />
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => doReply(c.id, false)} data-testid={`contact-reply-${c.id}`}
                      className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-[#003893] text-white text-xs font-semibold"><Reply className="w-3.5 h-3.5" /> Responder</button>
                    <button onClick={() => doReply(c.id, true)} data-testid={`contact-moreinfo-${c.id}`}
                      className="h-9 px-3 rounded-lg bg-[#FEF3C7] text-[#92400E] text-xs font-semibold">Pedir más información</button>
                    {!c.reporter_share_consent && (
                      <button onClick={() => act(c.id, "share-contact", "Tu contacto fue compartido.")} data-testid={`contact-share-${c.id}`}
                        className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-[#D1FAE5] text-[#065F46] text-xs font-semibold"><Share2 className="w-3.5 h-3.5" /> Autorizar compartir mi contacto</button>
                    )}
                    <button onClick={() => act(c.id, "discard", "Solicitud descartada.")} data-testid={`contact-discard-${c.id}`}
                      className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-[#F1F5F9] text-[#475569] text-xs font-semibold"><Trash2 className="w-3.5 h-3.5" /> Descartar</button>
                    <button onClick={() => act(c.id, "abuse", "Reporte de abuso enviado.")} data-testid={`contact-abuse-${c.id}`}
                      className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-[#FEE2E2] text-[#991B1B] text-xs font-semibold"><Flag className="w-3.5 h-3.5" /> Reportar abuso</button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
